import Phaser from 'phaser';
import { CampManager, Survivor, SurvivorClass } from '../systems/CampManager';
import { MetaManager } from '../systems/MetaManager';
import { SfxManager } from '../utils/SfxManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { ResourceBanner } from '../ui/ResourceBanner';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE DU DORTOIR ===

const CLASS_INFO: Record<SurvivorClass, { emoji: string; name: string; color: string }> = {
  warrior: { emoji: '⚔️', name: 'Guerrier', color: '#e74c3c' },
  ranger: { emoji: '🏹', name: 'Tireur', color: '#2ecc71' },
  medic: { emoji: '💊', name: 'Médecin', color: '#3498db' },
};

const HEADER_H = 70;

export class DormScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private camp!: CampManager;
  private meta!: MetaManager;
  private sfx = new SfxManager();

  constructor() {
    super({ key: 'DormScene' });
  }

  init(data: { camp?: CampManager; meta?: MetaManager }): void {
    this.camp = data?.camp ?? new CampManager();
    this.meta = data?.meta ?? new MetaManager();
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.drawUI();
  }

  private drawUI(): void {
    this.children.removeAll(true);

    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // Bandeau ressources
    new ResourceBanner(this, this.camp.getResources());

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 18, '🛏️ DORTOIR', {
      fontSize: '20px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const survivors = this.camp.getSurvivors();
    const alive = this.camp.getAliveSurvivors();
    this.add.text(GAME_CX, 45, `${alive.length} vivant(s) / ${survivors.length} total`, {
      fontSize: '12px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5).setDepth(11);

    const backBtn = this.add.text(15, 10, '← Camp', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 10, y: 6 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => startSceneWithFade(this, IslandCampFlag.campSceneKey(), { camp: this.camp, meta: this.meta }));
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);

    let y = 10;

    // Capitaine
    y = this.drawSurvivorCard(y, {
      id: 'captain', name: 'Vous — Le Capitaine', class: 'warrior', level: 1, alive: true,
    }, true);
    y += 15;

    // Survivants — séparation vivants / morts pour la lisibilité
    if (survivors.length === 0) {
      this.panel.add(this.add.text(GAME_CX, y + 20, 'Aucun survivant recruté.\n\nExplorez l\'archipel pour\ntrouver des alliés !', {
        fontSize: '13px', color: '#7f8c8d', fontFamily: FONT_BODY, align: 'center',
      }).setOrigin(0.5));
      y += 80;
    } else {
      const aliveList = survivors.filter(s => s.alive);
      const deadList = survivors.filter(s => !s.alive);

      // === VIVANTS ===
      const cap = this.camp.getSurvivorCapacity();
      this.panel.add(this.add.text(GAME_CX, y, `--- Vivants (${aliveList.length}/${cap}) ---`, {
        fontSize: '11px', color: '#2ecc71', fontFamily: FONT_BODY, fontStyle: 'bold',
      }).setOrigin(0.5));
      y += 22;
      if (aliveList.length === 0) {
        this.panel.add(this.add.text(GAME_CX, y + 8, '(aucun survivant vivant)', {
          fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY, fontStyle: 'italic',
        }).setOrigin(0.5));
        y += 30;
      } else {
        for (const s of aliveList) {
          y = this.drawSurvivorCard(y, s, false);
          y += 8;
        }
      }

      // === MORTS (uniquement si > 0) ===
      if (deadList.length > 0) {
        y += 15;
        this.panel.add(this.add.text(GAME_CX, y, `--- 💀 Tombés (${deadList.length}) ---`, {
          fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY, fontStyle: 'bold',
        }).setOrigin(0.5));
        y += 22;
        for (const s of deadList) {
          y = this.drawSurvivorCard(y, s, false);
          y += 8;
        }
      }
    }

    // Info recrutement
    y += 15;
    this.panel.add(this.add.text(GAME_CX, y, 'Explorez la carte pour recruter des survivants ! 👤', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setOrigin(0.5));

    y += 30;
    this.panel.setContentHeight(y);
  }

  shutdown(): void {
    this.panel?.destroy();
  }

  private drawSurvivorCard(y: number, survivor: Survivor, isCaptain: boolean): number {
    const info = CLASS_INFO[survivor.class];
    const alive = survivor.alive;
    const alpha = alive ? 1 : 0.4;

    // Layout responsive — desktop x=160 / x=550 préservés via formules (cardW=520).
    const cardW = Math.min(520, GAME_W - 20);
    const lx = GAME_CX - cardW / 2 + 20;          // 160 desktop, 30 mobile
    const deadX = GAME_CX + cardW / 2 - 110;      // 550 desktop, 110 mobile

    const bg = this.add.rectangle(GAME_CX, y + 20, cardW, 48, isCaptain ? 0x1a2a40 : 0x1e2d4a, alpha);
    bg.setStrokeStyle(1, isCaptain ? 0xf1c40f : 0x2a3a5c);
    this.panel.add(bg);

    this.panel.add(this.add.text(lx, y + 8, `${info.emoji} ${survivor.name}`, {
      fontSize: '13px', color: alive ? '#ecf0f1' : '#7f8c8d', fontFamily: FONT_BODY,
      fontStyle: isCaptain ? 'bold' : 'normal',
      wordWrap: { width: cardW - 130 },
    }));

    // Sprint 85 — étoiles visuelles selon le niveau (cap 5)
    const stars = '★'.repeat(Math.min(5, survivor.level)) + '☆'.repeat(Math.max(0, 5 - survivor.level));
    this.panel.add(this.add.text(lx, y + 26, `${info.name}  Niv.${survivor.level}  ${stars}`, {
      fontSize: '10px', color: alive ? info.color : '#6c7a89', fontFamily: FONT_BODY,
    }));

    if (!alive) {
      this.panel.add(this.add.text(deadX, y + 15, '💀 Mort', {
        fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      }));
    }

    return y + 48;
  }
}
